/**
 * Consent state, and AdSense loading.
 *
 * ## The rule this file actually enforces — corrected 2026-09-18
 *
 * **No ad slot is ever requested, no ad-personalization/targeting cookie is
 * set, and no Google Analytics cookie is set, until the user has actively
 * agreed.** One decision — asked once, through `ConsentBanner` — now drives
 * both: `hasAdConsent()` gates `pushAdSlot()`, and `pushConsentModeUpdate()`
 * (below) pushes both the ad and the `analytics_storage` Consent Mode
 * signals together. Before 2026-09-18 this file only ever asked about ads,
 * and `analytics_storage` was hard-coded `granted` regardless of the
 * answer — see `scripts/consentModeHead.ts` and `src/lib/ga.ts` for that
 * history and why it changed.
 *
 * This file does **not** control whether Google's base AdSense script itself
 * loads — that happens unconditionally, from a static `<head>` tag on every
 * page except `/resume-builder` (`scripts/prerender.ts`'s `adsenseHeadFor`),
 * independently of the user's consent choice. An earlier version of this
 * comment claimed "no third-party request is made until the user has
 * actively agreed to it" — that was wrong, and was live-verified wrong: the
 * base script executes and pings Google's ad-traffic-quality endpoint on
 * page load, before any consent answer exists. Verified
 * 2026-08-28; see `docs/privacy-consent.md` for the summary and how to
 * reproduce it.
 *
 * Note: an earlier draft of this correction also asserted that the base
 * script loads unconditionally "for Auto-ads verification reasons." That
 * causal claim is not supported by any official Google source verified
 * (see the policy references in `docs/privacy-consent.md`) and has been removed — only the observed
 * behavior (loads regardless of consent, on every page but the builder) is
 * asserted here now.
 *
 * ## What this is not
 *
 * This is **not** a Google-certified CMP and does not implement IAB TCF v2.2.
 * Google requires a certified CMP for EEA/UK/Swiss traffic, and that has to be a
 * third-party product (Google's own Privacy & Messaging, or a certified vendor).
 * What this provides is the *gate* those products plug into: a single choke
 * point that no ad code can bypass. See docs/privacy-consent.md.
 *
 * ## Signals honoured before any banner is shown
 *
 * Do Not Track and Global Privacy Control are treated as a refusal, so a user
 * who has already expressed the preference at browser level is never asked
 * again. GPC in particular is legally binding in several US states.
 */

import { SITE } from '@/config/site'
import { CONSENT_MODE, inConsentRegion } from '@/config/consent'
import { isProductionHost } from '@/lib/ga'

/**
 * Versioned (`:v2`) because its scope changed 2026-09-18: this key used to
 * record an ads-only answer, and analytics respected no consent gate at all.
 * Bumping the key means every visitor — including one who already answered
 * under the old, narrower question — is asked again under the banner's
 * current, accurate wording, rather than an old "yes" to ads-only silently
 * being reinterpreted as consent for analytics it was never asked about.
 */
const CONSENT_KEY = 'ats-resume:consent:v2'

export type ConsentState = 'granted' | 'denied' | 'unset'

/** Is advertising configured at all? Empty publisher id means a clean fork. */
export const adsConfigured = (): boolean => SITE.adsenseClientId.trim() !== ''

/**
 * A browser-level refusal. Checked before anything is stored or displayed, so a
 * user with DNT or GPC set is never shown a consent prompt at all — asking again
 * after they have already answered is the dark pattern this avoids.
 */
export const hasGlobalOptOut = (): boolean => {
  if (typeof navigator === 'undefined') return false
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean; msDoNotTrack?: string }
  return (
    nav.globalPrivacyControl === true ||
    nav.doNotTrack === '1' ||
    nav.msDoNotTrack === '1' ||
    (typeof window !== 'undefined' &&
      (window as Window & { doNotTrack?: string }).doNotTrack === '1')
  )
}

export const readConsent = (): ConsentState => {
  if (typeof window === 'undefined') return 'unset'
  if (hasGlobalOptOut()) return 'denied'
  try {
    const stored = window.localStorage.getItem(CONSENT_KEY)
    return stored === 'granted' || stored === 'denied' ? stored : 'unset'
  } catch {
    // Storage blocked. Treat as no consent — the safe direction.
    return 'denied'
  }
}

/**
 * Pushes every regulated Consent Mode v2 signal — ad *and* analytics — from
 * one decision.
 *
 * Before 2026-09-18, `analytics_storage` was deliberately excluded here and
 * stayed hard-coded `granted` in `scripts/consentModeHead.ts`, because this
 * banner only ever asked about ads. That no longer matches this site's own
 * Privacy Policy, which describes Google Analytics as consent-gated — so
 * this single answer now governs both. Guarded on `typeof gtag ===
 * 'function'` because the default-state script that defines it is skipped in
 * a clean fork with no GA id and no AdSense id configured (both empty) —
 * nothing reads these signals then either.
 */
const pushConsentModeUpdate = (state: ConsentState): void => {
  const w = window as Window & { gtag?: (...args: unknown[]) => void }
  if (typeof w.gtag !== 'function') return
  const granted = state === 'granted'
  w.gtag('consent', 'update', {
    ad_storage: granted ? 'granted' : 'denied',
    ad_user_data: granted ? 'granted' : 'denied',
    ad_personalization: granted ? 'granted' : 'denied',
    analytics_storage: granted ? 'granted' : 'denied',
  })
}

/**
 * Reconciles Google Consent Mode with whatever is already in localStorage.
 *
 * The default-state script in <head> always starts every ad signal denied —
 * it runs before localStorage can be read. A returning visitor who already
 * granted consent needs that reflected on this page load too, not just on
 * their next banner answer: `AdSlot` independently checks `hasAdConsent()`
 * and will load the ad script regardless, so without this call Consent Mode
 * would keep reporting "denied" for a visitor Google's own tags could
 * otherwise treat as consented. Called once on mount by `ConsentBanner`,
 * which renders on every route.
 *
 * Skips `'unset'` (no answer yet) — **fixed 2026-09-19**. Calling
 * `gtag('consent','update', ...)` for a visitor who hasn't decided anything
 * is not a "sync", it is an explicit (and false) denial: the default script
 * already declares every signal denied *and* tells gtag.js to hold pings for
 * up to `wait_for_update: 500`ms in case a real answer arrives. Pushing an
 * `update` here — even one whose values match the default — cancels that
 * wait immediately, so the page's very first `page_view` was being locked
 * into a cookieless/modelled hit before the banner had even rendered,
 * regardless of how fast the visitor went on to click "Allow". Confirmed
 * live: the resulting hit carried `gcs=G100`/`pscdl=denied` within
 * milliseconds of load. Only a real, stored answer needs reconciling here.
 */
export const syncConsentMode = (): void => {
  const state = readConsent()
  if (state === 'unset') return
  pushConsentModeUpdate(state)
}

/**
 * Deletes the GA4 cookies (`_ga`, `_ga_<id>`) an earlier Allow set.
 *
 * Added 2026-10-21 with `openConsentChoices`: once a visitor can change their answer,
 * Decline has to undo what Allow did, not only stop new cookie writes. Consent
 * Mode `denied` stops gtag reading or writing them, but the cookies themselves
 * would otherwise sit there until they expire (two years). GA sets them on the
 * registrable domain (`.atsresumekit.com`, observed live), so both the host-only
 * and the dotted-domain form are expired.
 */
const clearAnalyticsCookies = (): void => {
  if (typeof document === 'undefined') return
  const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/'
  for (const pair of document.cookie.split(';')) {
    const name = pair.split('=')[0].trim()
    if (name !== '_ga' && !name.startsWith('_ga_')) continue
    document.cookie = `${name}=; ${expired}`
    document.cookie = `${name}=; ${expired}; domain=.${window.location.hostname}`
  }
}

export const writeConsent = (state: Exclude<ConsentState, 'unset'>): void => {
  try {
    window.localStorage.setItem(CONSENT_KEY, state)
  } catch {
    /* Blocked storage means the choice is not remembered; ads stay off. */
  }
  pushConsentModeUpdate(state)
  if (state === 'denied') clearAnalyticsCookies()
  window.dispatchEvent(new CustomEvent('ats:consent', { detail: state }))
}

/**
 * Reopens the consent banner on request: the footer "Cookie choices" button and
 * the /privacy "Change your choice" button (added 2026-10-21; before that the
 * only way to withdraw consent was clearing site data).
 *
 * The stored answer is deliberately kept until the visitor picks again, so
 * closing the page without answering leaves their earlier choice in force, and
 * no Consent Mode update is pushed here. The banner's own `writeConsent` sends
 * the new state, and on Decline also clears `_ga`; `src/lib/ahrefs.ts` stops
 * sending page views from the next page on.
 */
export const openConsentChoices = (): void => {
  window.dispatchEvent(new CustomEvent('ats:consent-open'))
}

/**
 * What this visitor's answer counts as, including no answer yet.
 *
 * In `strict` mode (src/config/consent.ts) that is just the stored answer:
 * unanswered means not granted. In `regional` mode a visitor outside the
 * consent regions is opted in until they decline through "Cookie choices",
 * matching the granted Consent Mode default Google applies to them. Do Not
 * Track / GPC are already folded into `readConsent()` as `denied`.
 */
export const effectiveConsent = (): Exclude<ConsentState, 'unset'> => {
  const state = readConsent()
  if (state !== 'unset') return state
  return CONSENT_MODE === 'regional' && !inConsentRegion() ? 'granted' : 'denied'
}

export const hasAdConsent = (): boolean => adsConfigured() && effectiveConsent() === 'granted'

/** May Google Analytics set cookies and receive product events for this visitor? */
export const hasAnalyticsConsent = (): boolean => effectiveConsent() === 'granted'

/** Is Google Analytics configured at all? Empty measurement id means a clean fork. */
const analyticsConfigured = (): boolean => SITE.gaMeasurementId.trim() !== ''

/**
 * Should we ask?
 *
 * Only when there is something to ask about — ads or analytics configured —
 * the user has not already answered, and no browser-level refusal is
 * present. Everything else means no banner.
 *
 * Analytics joined this question 2026-09-18 (see `pushConsentModeUpdate`):
 * a deployment with GA configured but no AdSense client id (or vice versa)
 * still needs to ask, since either one alone is something to gate.
 */
/** Is there anything for the consent question to govern on this deployment? */
export const consentApplies = (): boolean => adsConfigured() || analyticsConfigured()

export const shouldAskConsent = (): boolean =>
  consentApplies() &&
  !hasGlobalOptOut() &&
  readConsent() === 'unset' &&
  // `regional` mode asks only visitors in the consent regions; see src/config/consent.ts.
  (CONSENT_MODE === 'strict' || inConsentRegion())

/* ---------------------------------------------------------------------------
 * Script loading
 * ------------------------------------------------------------------------ */

let loading: Promise<void> | null = null

/** Appends the AdSense script once per page; resolves when it has loaded. */
const injectAdSenseScript = (): Promise<void> => {
  if (loading) return loading
  loading = new Promise<void>((resolve, reject) => {
    if (document.querySelector('script[data-adsense]')) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(SITE.adsenseClientId)}`
    script.async = true
    script.crossOrigin = 'anonymous'
    script.dataset.adsense = 'true'
    script.onload = () => resolve()
    script.onerror = () => {
      // Blocked by an ad blocker or by CSP. Not an error worth surfacing —
      // the slot simply stays empty and the page is unaffected.
      loading = null
      reject(new Error('adsense-blocked'))
    }
    document.head.appendChild(script)
  })
  return loading
}

/**
 * Injects the AdSense tag exactly once, and only with consent.
 *
 * Deliberately not in `index.html`. A script tag in the document would fetch
 * from googlesyndication.com on *every* page load — including
 * `/resume-builder`, and including for users who declined — which is
 * precisely the behaviour this product tells people to verify does not
 * happen.
 *
 * Production host only: `vite preview`, `vite dev` and test runs serve this
 * same code, and must not load the real AdSense script.
 */
export const loadAdSense = (): Promise<void> => {
  if (!hasAdConsent()) return Promise.resolve()
  if (!isProductionHost(window.location.hostname)) return Promise.resolve()
  return injectAdSenseScript()
}

/** Requests a fill for one `<ins>` element. Safe to call before the script loads. */
export const pushAdSlot = (): void => {
  if (!hasAdConsent()) return
  try {
    const w = window as Window & { adsbygoogle?: unknown[] }
    w.adsbygoogle = w.adsbygoogle || []
    w.adsbygoogle.push({})
  } catch {
    /* Blocked or not yet available — the slot stays empty. */
  }
}
